import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HomeShowcase } from "@/components/HomeShowcase";
import { InstallButton } from "@/components/InstallButton";
import { SECTIONS } from "@/lib/sections";
import { TOOLS } from "@/components/ToolPrompts";

const LandingNav = () => (
  <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b rf-line">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 h-16 flex items-center gap-3">
      <span className="w-3.5 h-3.5 bg-[var(--rf-red)]" />
      <span className="font-extrabold tracking-tight">Reverse Frame</span>
      <div className="ml-auto flex items-center gap-3">
        <InstallButton className="rf-btn-ghost" compact />
        <Link to="/login" className="rf-btn rf-btn-sm" data-testid="landing-nav-login">Accedi</Link>
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section className="grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-8 py-16 lg:py-24 border-b border-[var(--rf-ink)]">
    <div className="lg:col-span-8 rf-fade">
      <p className="rf-mono text-[11px] tracking-[0.3em] uppercase rf-red mb-6">Reverse editing con l'IA</p>
      <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.92]" data-testid="landing-title">
        Ogni immagine è un prompt <span className="rf-red">nascosto.</span>
      </h1>
    </div>
    <div className="lg:col-span-4 lg:self-end space-y-6 rf-fade" style={{ animationDelay: "120ms" }}>
      <p className="text-base sm:text-lg leading-relaxed" data-testid="landing-subtitle">
        Carica una foto o una clip: Reverse Frame la analizza come un direttore della fotografia e ti restituisce il prompt per ricrearla con l'IA.
      </p>
      <div className="flex flex-wrap gap-3">
        <Link to="/login" className="rf-btn" data-testid="landing-cta-start">Inizia ora <ArrowRight size={15} /></Link>
        <InstallButton className="rf-btn-ghost" />
      </div>
    </div>
  </section>
);

const SixSections = () => (
  <section className="py-16 border-b rf-line" data-testid="landing-sections">
    <h2 className="text-lg font-bold tracking-tight mb-8">Cosa ottieni da ogni file</h2>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10">
      {SECTIONS.map((s) => (
        <div key={s.n} className="py-6 border-t border-[var(--rf-ink)]">
          <span className={`text-4xl font-black tracking-tighter ${s.n === 6 ? "rf-red" : ""}`}>{String(s.n).padStart(2, "0")}</span>
          <p className="font-bold mt-2">{s.it}</p>
          <p className="rf-mono text-[10px] tracking-[0.15em] uppercase text-[var(--rf-grey)] mt-1">{s.en}</p>
        </div>
      ))}
    </div>
  </section>
);

const ToolsStrip = () => (
  <section className="py-16 border-b rf-line" data-testid="landing-tools">
    <h2 className="text-lg font-bold tracking-tight">Un prompt su misura per ogni IA</h2>
    <p className="text-sm text-[var(--rf-grey)] mt-2 max-w-xl">Con un tocco il prompt viene riscritto nel formato preferito da ogni strumento.</p>
    <div className="flex flex-wrap gap-2 mt-8">
      {TOOLS.map((t) => (
        <span key={t.id} className="px-3 py-2 border border-[var(--rf-line)]">
          <span className="block text-sm font-semibold">{t.label}</span>
          <span className="block rf-mono text-[9px] tracking-wider uppercase text-[var(--rf-grey)]">{t.kind}</span>
        </span>
      ))}
    </div>
  </section>
);

const FinalCta = () => (
  <section className="py-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
    <h2 className="lg:col-span-8 text-4xl sm:text-5xl font-extrabold tracking-tight leading-[0.95]">Scomponi. Ricostruisci.<br /><span className="rf-red">Rigenera.</span></h2>
    <div className="lg:col-span-4 flex flex-wrap gap-3">
      <Link to="/login" className="rf-btn" data-testid="landing-cta-bottom">Crea il tuo account <ArrowRight size={15} /></Link>
    </div>
  </section>
);

export default function Landing() {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <LandingNav />
      <main className="max-w-[1400px] mx-auto px-5 sm:px-10">
        <Hero />
        <div className="py-16 border-b rf-line"><HomeShowcase /></div>
        <SixSections />
        <ToolsStrip />
        <FinalCta />
      </main>
      <footer className="border-t rf-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-6 flex flex-wrap gap-4 rf-mono text-[10px] tracking-[0.2em] uppercase text-[var(--rf-grey)]">
          <span>© {new Date().getFullYear()} Reverse Frame</span>
          <span className="ml-auto">reverse-editing.shop</span>
        </div>
      </footer>
    </div>
  );
}
